import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import {
  useFetchCompanies,
  useFetchDepartments,
  useFetchJobTitles,
  useFetchPersonaById,
} from '../../api/apiHooks'
import { CompanionUpdateInput, FilterOptions } from '../../api/types'
import { AutoComplete, AutoCompleteOption } from '../Autocomplete'
import { TextAreaField } from '../ControlledTextAreaField'
import { FormInput } from '../FormInput'
import { ImageUploader } from '../ImageUpload'

interface MentorInput extends CompanionUpdateInput {
  previewImageFile?: File
}

export interface MentorEditFormProps {
  id: string
  value: MentorInput
  onSubmit?: (data: MentorInput) => void
  onModalClose?: () => void
  filters?: FilterOptions
}

export function MentorEditForm({
  id,
  value,
  onSubmit,
  onModalClose,
  filters,
}: MentorEditFormProps) {
  const { data, isFetching } = useFetchPersonaById(id, false)
  const defaultValues = useMemo<MentorInput>(() => {
    return {
      mentorId: value.mentorId || id,
      callToAction:
        value.callToAction || data?.data.profile.profile.call_to_action,
      company: value.company || data?.data.profile.profile.company,
      department: value.department || data?.data.profile.profile.department,
      firstName: value.firstName || data?.data.profile.profile.firstName,
      gender: value.gender || data?.data.profile.profile.gender,
      headline: value.headline || data?.data.profile.profile.headline,
      industry: value.industry || data?.data.profile.profile.industry,
      jobTitle: value.jobTitle || data?.data.profile.profile.jobTitle,
      lastName: value.lastName || data?.data.profile.profile.lastName,
      location: value.location || data?.data.profile.profile.location,
      race: value.race || data?.data.profile.profile.race,
      slug: value.slug || data?.data.profile.profile.slug,
      previewUrl:
        value.previewUrl || data?.data.profile.profile.backgroundImageUrl || '',
      previewImageFile: undefined,
    }
  }, [value, data?.data.profile.profile, id])

  const [searchValue, setSearchValue] = useState({
    company: '',
    jobTitle: '',
    department: '',
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting, dirtyFields },
  } = useForm<MentorInput>({ defaultValues })

  const formValues = watch()
  const {
    gender,
    race,
    company,
    jobTitle,
    department,
    industry,
    previewUrl: watchedPreviewUrl,
  } = formValues

  const {
    data: jobTitles,
    isFetching: isJobTitlesFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useFetchJobTitles(searchValue.jobTitle, !!searchValue.jobTitle)

  const {
    data: companies,
    isFetching: isCompaniesFetching,
    hasNextPage: hasCompaniesNextPage,
    isFetchingNextPage: isCompaniesFetchingNextPage,
    fetchNextPage: fetchCompaniesNextPage,
  } = useFetchCompanies(searchValue.company, !!searchValue.company)

  const {
    data: departments,
    isFetching: isDepartmentsFetching,
    hasNextPage: hasDepartmentsNextPage,
    isFetchingNextPage: isDepartmentsFetchingNextPage,
    fetchNextPage: fetchDepartmentsNextPage,
  } = useFetchDepartments(searchValue.department, !!searchValue.department)

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  // Memoized form submit handler
  const onFormSubmit = useCallback(
    (data: MentorInput) => {
      const changedFields: Partial<MentorInput> = {}
      Object.keys(dirtyFields).forEach(key => {
        changedFields[key as keyof MentorInput] = data[key as keyof MentorInput]
      })
      changedFields.mentorId = data.mentorId
      onSubmit?.(changedFields as MentorInput)
    },
    [dirtyFields, onSubmit],
  )

  // Memoize API data transformations to prevent unnecessary recalculations
  const jobTitlesSearchResult = useMemo(
    () =>
      jobTitles?.pages
        .flatMap(page => page.items)
        .map(j => ({
          label: j,
          value: j,
        })) || [],
    [jobTitles?.pages],
  )

  const companiesSearchResult = useMemo(
    () =>
      companies?.pages
        .flatMap(page => page.items)
        .map(c => ({
          label: c,
          value: c,
        })) || [],
    [companies?.pages],
  )

  const departmentsSearchResult = useMemo(
    () =>
      departments?.pages
        .flatMap(page => page.items)
        .map(d => ({
          label: d,
          value: d,
        })) || [],
    [departments?.pages],
  )

  // Memoize filter options to prevent unnecessary recalculations
  const genderOptions = useMemo(
    () => filters?.gender?.map(g => ({ label: g, value: g })) || [],
    [filters?.gender],
  )

  const raceOptions = useMemo(
    () => filters?.race?.map(r => ({ label: r, value: r })) || [],
    [filters?.race],
  )

  const industryOptions = useMemo(
    () => filters?.industry?.map(i => ({ label: i, value: i })) || [],
    [filters?.industry],
  )

  // Memoized AutoComplete value creators to prevent object recreation
  const createSelectValue = useCallback(
    (value: string | undefined) =>
      value ? { label: value, value: value } : null,
    [],
  )

  // Memoized onChange handlers for better performance
  const handleGenderChange = useCallback(
    (value: AutoCompleteOption | null) => {
      setValue('gender', value?.value as string, {
        shouldValidate: true,
        shouldDirty: defaultValues.gender !== value?.value,
      })
    },
    [setValue, defaultValues.gender],
  )

  const handleRaceChange = useCallback(
    (value: AutoCompleteOption | null) => {
      setValue('race', value?.value as string, {
        shouldValidate: true,
        shouldDirty: defaultValues.race !== value?.value,
      })
    },
    [setValue, defaultValues.race],
  )

  const handleCompanyChange = useCallback(
    (value: AutoCompleteOption | null) => {
      setValue('company', value?.value as string, {
        shouldValidate: true,
        shouldDirty: defaultValues.company !== value?.value,
      })
      setSearchValue(prev => ({ ...prev, company: '' }))
    },
    [setValue, defaultValues.company],
  )

  const handleJobTitleChange = useCallback(
    (value: AutoCompleteOption | null) => {
      setValue('jobTitle', value?.value as string, {
        shouldValidate: true,
        shouldDirty: defaultValues.jobTitle !== value?.value,
      })
      setSearchValue(prev => ({ ...prev, jobTitle: '' }))
    },
    [setValue, defaultValues.jobTitle],
  )

  const handleDepartmentChange = useCallback(
    (value: AutoCompleteOption | null) => {
      setValue('department', value?.value as string, {
        shouldValidate: true,
        shouldDirty: defaultValues.department !== value?.value,
      })
      setSearchValue(prev => ({ ...prev, department: '' }))
    },
    [setValue, defaultValues.department],
  )

  const handleIndustryChange = useCallback(
    (value: AutoCompleteOption | null) => {
      setValue('industry', value?.value as string, {
        shouldValidate: true,
        shouldDirty: defaultValues.industry !== value?.value,
      })
    },
    [setValue, defaultValues.industry],
  )

  // Memoized search handlers to prevent function recreation
  const handleCompanySearch = useCallback(
    (text: string) => {
      setSearchValue(prev => ({ ...prev, company: text }))
      return companiesSearchResult || []
    },
    [companiesSearchResult],
  )

  const handleJobTitleSearch = useCallback(
    (text: string) => {
      setSearchValue(prev => ({ ...prev, jobTitle: text }))
      return jobTitlesSearchResult || []
    },
    [jobTitlesSearchResult],
  )

  const handleDepartmentSearch = useCallback(
    (text: string) => {
      setSearchValue(prev => ({ ...prev, department: text }))
      return departmentsSearchResult || []
    },
    [departmentsSearchResult],
  )

  // Memoized image upload handler
  const handleImageChange = useCallback(
    (url: string, file?: File) => {
      setValue('previewUrl', url, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('previewImageFile', file, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  return (
    <div className="p-4 relative">
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4 mt-4">
        <div className="grid grid-cols-1 gap-6 mb-6">
          <TextAreaField
            label="Headline"
            name="headline"
            register={register}
            error={errors.headline?.message}
            rows={3}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="First Name"
              name="firstName"
              register={register}
              error={errors.firstName?.message}
              type="text"
            />
            <FormInput
              label="Last Name"
              name="lastName"
              register={register}
              error={errors.lastName?.message}
              type="text"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AutoComplete
              label="Gender"
              name="gender"
              value={createSelectValue(gender)}
              error={errors.gender?.message}
              selectOnly
              allowClear={false}
              onChange={handleGenderChange}
              options={genderOptions}
            />

            <AutoComplete
              label="Race"
              name="race"
              error={errors.race?.message}
              value={createSelectValue(race)}
              onChange={handleRaceChange}
              selectOnly
              allowClear={false}
              options={raceOptions}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AutoComplete
              label="Company"
              name="company"
              freeSolo
              error={errors.company?.message}
              value={createSelectValue(company)}
              onChange={handleCompanyChange}
              allowClear={false}
              options={companiesSearchResult}
              isLoading={isCompaniesFetching}
              fetchNextPage={fetchCompaniesNextPage}
              needFetchNextPage={
                hasCompaniesNextPage && !isCompaniesFetchingNextPage
              }
              onSearch={handleCompanySearch}
            />

            <AutoComplete
              label="Job Title"
              name="jobTitle"
              freeSolo
              error={errors.jobTitle?.message}
              value={createSelectValue(jobTitle)}
              onChange={handleJobTitleChange}
              options={jobTitlesSearchResult}
              isLoading={isJobTitlesFetching}
              fetchNextPage={fetchNextPage}
              needFetchNextPage={hasNextPage && !isFetchingNextPage}
              allowClear={false}
              onSearch={handleJobTitleSearch}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AutoComplete
              label="Department"
              name="department"
              freeSolo
              error={errors.department?.message}
              value={createSelectValue(department)}
              onChange={handleDepartmentChange}
              options={departmentsSearchResult}
              isLoading={isDepartmentsFetching}
              fetchNextPage={fetchDepartmentsNextPage}
              needFetchNextPage={
                hasDepartmentsNextPage && !isDepartmentsFetchingNextPage
              }
              allowClear={false}
              onSearch={handleDepartmentSearch}
            />

            <AutoComplete
              label="Industry"
              name="industry"
              value={createSelectValue(industry)}
              error={errors.industry?.message}
              allowClear={false}
              onChange={handleIndustryChange}
              options={industryOptions}
              freeSolo
            />
          </div>
          <FormInput
            label="Location"
            name="location"
            register={register}
            error={errors.location?.message}
            type="text"
          />
          <FormInput
            label="Call to Action"
            name="callToAction"
            register={register}
            error={errors.callToAction?.message}
            type="text"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor={`backgroundImageUrl-${id}`}
                className="block mb-2 text-white"
              >
                Profile Image
              </label>
              <ImageUploader
                id={`backgroundImageUrl-${id}`}
                value={watchedPreviewUrl as string}
                onChange={handleImageChange}
                error={errors.previewUrl?.message}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-4 border-t border-white/10 py-4 px-6">
          <button
            type="reset"
            onClick={onModalClose}
            className="btn btn-secondary transition-bg"
          >
            Close
          </button>
          <button
            type="submit"
            disabled={!isDirty || isSubmitting}
            className="btn btn-primary transition-bg"
          >
            {isSubmitting && <AnimatedSpinner className="mr-2" />}
            Save
          </button>
        </div>
      </form>
      {isFetching && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <AnimatedSpinner className="text-white" />
        </div>
      )}
    </div>
  )
}
